import { create } from 'zustand';
import { api } from '@/lib/api';
import {
  getAccessToken,
  setAccessToken,
  getRefreshToken,
  setRefreshToken,
  clearTokens,
  parseJWT,
  isTokenExpired,
  saveOriginalTokens,
  getOriginalTokens,
  clearOriginalTokens,
  isImpersonating as checkIsImpersonating,
} from '@/lib/auth';

interface User {
  id: string;
  email: string;
  displayName: string;
  role: string;
  permissions: Record<string, boolean>;
  tier: string;
  isCurator?: boolean;
  curatorType?: string | null;
}

interface AuthTokenResponse {
  accessToken: string;
  refreshToken?: string;
  user?: User;
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isImpersonating: boolean;
  menuAccess: Record<string, boolean>;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, displayName: string, gdprConsent: boolean, userType?: string, city?: string, showAddress?: boolean, returnTo?: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshAuth: () => Promise<void>;
  checkAuth: () => Promise<void>;
  loadMenuAccess: () => Promise<void>;
  impersonateUser: (userId: string) => Promise<void>;
  returnToAdmin: () => Promise<void>;
}

function userFromToken(token: string): User | null {
  const payload = parseJWT(token);
  if (!payload) return null;
  return {
    id: payload.sub || '',
    email: (payload.email as string) || '',
    displayName: (payload.displayName as string) || '',
    role: (payload.role as string) || 'user',
    permissions: (payload.permissions as unknown as Record<string, boolean>) || {},
    tier: (payload.tier as string) || 'free',
  };
}

async function fetchMenuAccess(): Promise<Record<string, boolean>> {
  try {
    const data = await api.get<{ tier: string; menuAccess: Record<string, boolean> }>('/tiers/menu-access');
    return data.menuAccess || {};
  } catch {
    return {};
  }
}

async function fetchCuratorStatus(): Promise<{ isCurator: boolean; curatorType: string | null }> {
  try {
    const data = await api.get<{ is_curator: boolean; curator_type: string | null }>('/auth/curator');
    return { isCurator: !!data.is_curator, curatorType: data.curator_type || null };
  } catch {
    return { isCurator: false, curatorType: null };
  }
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  isAuthenticated: false,
  isLoading: true,
  isImpersonating: checkIsImpersonating(),
  menuAccess: {},

  loadMenuAccess: async () => {
    const [menuAccess, curator] = await Promise.all([fetchMenuAccess(), fetchCuratorStatus()]);
    set((s) => ({ menuAccess, user: s.user ? { ...s.user, isCurator: curator.isCurator, curatorType: curator.curatorType } : null }));
  },

  login: async (email: string, password: string) => {
    const data = await api.post<AuthTokenResponse>('/auth/login', { email, password });
    setAccessToken(data.accessToken);
    if (data.refreshToken) setRefreshToken(data.refreshToken);
    const user = data.user || userFromToken(data.accessToken);
    set({ user, isAuthenticated: true });
    const [menuAccess, curator] = await Promise.all([fetchMenuAccess(), fetchCuratorStatus()]);
    set({ menuAccess, user: user ? { ...user, isCurator: curator.isCurator, curatorType: curator.curatorType } : null });
  },

  register: async (email: string, password: string, displayName: string, gdprConsent: boolean, userType?: string, city?: string, showAddress: boolean = true, returnTo?: string) => {
    const data = await api.post<AuthTokenResponse>('/auth/register', {
      email,
      password,
      displayName,
      gdprConsent,
      userType: userType || 'artist',
      city: city || undefined,
      showAddress,
      returnTo: returnTo || undefined,
    });
    setAccessToken(data.accessToken);
    if (data.refreshToken) setRefreshToken(data.refreshToken);
    const user = data.user || userFromToken(data.accessToken);
    set({ user, isAuthenticated: true });
    const [menuAccess, curator] = await Promise.all([fetchMenuAccess(), fetchCuratorStatus()]);
    set((s) => ({ menuAccess, user: s.user ? { ...s.user, isCurator: curator.isCurator, curatorType: curator.curatorType } : null }));

    // Set Near Me store with the registered city so map zooms there immediately
    if (city?.trim()) {
      const { useNearMeStore } = await import('./nearMeStore');
      useNearMeStore.getState().setUserTown(city.trim());
      useNearMeStore.getState().activateWithUserTown();
    }
  },

  logout: async () => {
    try {
      await api.post('/auth/logout');
    } catch {
      // Ignore errors on logout
    }
    clearTokens();
    clearOriginalTokens();
    set({ user: null, isAuthenticated: false, isImpersonating: false, menuAccess: {} });
  },

  refreshAuth: async () => {
    try {
      const refreshTok = getRefreshToken();
      if (!refreshTok) throw new Error('No refresh token');
      const data = await api.post<AuthTokenResponse>('/auth/refresh', { refreshToken: refreshTok });
      setAccessToken(data.accessToken);
      if (data.refreshToken) setRefreshToken(data.refreshToken);
      const user = data.user || userFromToken(data.accessToken);
      set({ user, isAuthenticated: true });
      const [menuAccess, curator] = await Promise.all([fetchMenuAccess(), fetchCuratorStatus()]);
      set((s) => ({ menuAccess, user: s.user ? { ...s.user, isCurator: curator.isCurator, curatorType: curator.curatorType } : null }));
    } catch {
      clearTokens();
      set({ user: null, isAuthenticated: false, menuAccess: {} });
    }
  },

  checkAuth: async () => {
    const token = getAccessToken();
    if (!token) {
      set({ user: null, isAuthenticated: false, isLoading: false });
      return;
    }

    if (isTokenExpired(token)) {
      try {
        const refreshTok = getRefreshToken();
        if (!refreshTok) throw new Error('No refresh token');
        const data = await api.post<AuthTokenResponse>('/auth/refresh', { refreshToken: refreshTok });
        setAccessToken(data.accessToken);
        if (data.refreshToken) setRefreshToken(data.refreshToken);
        const user = data.user || userFromToken(data.accessToken);
        set({ user, isAuthenticated: true, isLoading: false, isImpersonating: checkIsImpersonating() });
        const [menuAccess, curator] = await Promise.all([fetchMenuAccess(), fetchCuratorStatus()]);
        set((s) => ({ menuAccess, user: s.user ? { ...s.user, isCurator: curator.isCurator, curatorType: curator.curatorType } : null }));
      } catch {
        clearTokens();
        set({ user: null, isAuthenticated: false, isLoading: false });
      }
    } else {
      const user = userFromToken(token);
      set({ user, isAuthenticated: !!user, isLoading: false, isImpersonating: checkIsImpersonating() });
      if (user) {
        const [menuAccess, curator] = await Promise.all([fetchMenuAccess(), fetchCuratorStatus()]);
        set((s) => ({ menuAccess, user: s.user ? { ...s.user, isCurator: curator.isCurator, curatorType: curator.curatorType } : null }));
      }
    }
  },

  impersonateUser: async (userId: string) => {
    saveOriginalTokens();
    const data = await api.post<AuthTokenResponse>(`/admin/users/${userId}/impersonate`);
    setAccessToken(data.accessToken);
    if (data.refreshToken) setRefreshToken(data.refreshToken);
    const user = data.user || userFromToken(data.accessToken);
    set({ user, isAuthenticated: true, isImpersonating: true });
    const [menuAccess, curator] = await Promise.all([fetchMenuAccess(), fetchCuratorStatus()]);
    set((s) => ({ menuAccess, user: s.user ? { ...s.user, isCurator: curator.isCurator, curatorType: curator.curatorType } : null }));
  },

  returnToAdmin: async () => {
    const original = getOriginalTokens();
    if (!original.accessToken) return;

    setAccessToken(original.accessToken);
    if (original.refreshToken) setRefreshToken(original.refreshToken);
    clearOriginalTokens();

    const user = userFromToken(original.accessToken);
    set({ user, isAuthenticated: !!user, isImpersonating: false });

    // Refresh if original token expired, then reload menu access
    if (original.accessToken && isTokenExpired(original.accessToken) && original.refreshToken) {
      await get().refreshAuth();
    }
    const [menuAccess, curator] = await Promise.all([fetchMenuAccess(), fetchCuratorStatus()]);
    set((s) => ({ menuAccess, user: s.user ? { ...s.user, isCurator: curator.isCurator, curatorType: curator.curatorType } : null }));
  },
}));
